import React from "react";
import { useDispatch } from "react-redux";

import useMediaQuery from "@material-ui/core/useMediaQuery";
import useTheme from "@material-ui/core/styles/useTheme";

import ActionColumnHeaders from "components/VetExamsTable/TableHeader/ActionColumnHeaders";
import TableHeader from "components/VetExamsTable/TableHeader";
import { getSortDirection } from "components/VetExamsTable/TableHeader/helper";
import {
  ERowColumn,
  ITableHeaderColumnsContent
} from "components/VetExamsTable/types";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { checkSortingType } from "utils/sort-utils";
import { SortOrder, SortOrderType } from "interfaces/SortOrder";
import {
  setOrder as setStoreOrder,
  setOrderBy as setStoreOrderBy
} from "store/actions/vetWorkoutsPage";
import { EColumnName } from "../options";
import { checkCurrentWorkoutsTableTab } from "../../helper";

interface IProps {
  columnsClassName: string;
  columns: string[];
  refElement: HTMLElement;
}

const REQUESTS_TAB_ACTION_COLUMNS = ["Exam", "Pass", "Reject"];
const EXAMS_TAB_ACTION_COLUMNS = ["Assign"];

const WorkoutsTableHeader = (props: IProps) => {
  const { columnsClassName, columns, refElement } = props;

  const theme = useTheme();
  const matchesDownSx = useMediaQuery(theme.breakpoints.down("sx"), {
    noSsr: true
  });

  const dispatch = useDispatch();
  const { selectedTab, order, orderBy } = useAppSelector(
    state => state.vetWorkoutsPage
  );

  const { isFailedTab, isPassedTab, isRequestsTab, isExamsTab, isRejectedTab } =
    checkCurrentWorkoutsTableTab(selectedTab.value);
  const shouldShowAssignedVetColumn = [
    isPassedTab,
    isFailedTab,
    isExamsTab
  ].some(Boolean);

  const handleSort = (
    columnName: EColumnName,
    sortDirection: SortOrderType
  ) => {
    const { isAsc } = checkSortingType(sortDirection);
    const sortOrder = isAsc ? SortOrder.ASC : SortOrder.DESC;

    dispatch(setStoreOrderBy(columnName));
    dispatch(setStoreOrder(sortOrder));
  };

  const tableHeaderColumnsContent: ITableHeaderColumnsContent = {
    [ERowColumn.HORSE_AND_TRAINER]: [
      {
        title: "Horse",
        isDisplayed: true,
        sortableCell: {
          name: EColumnName.HORSE_NAME,
          sortOrder: getSortDirection(EColumnName.HORSE_NAME, orderBy, order),
          sortHandler: handleSort
        }
      },
      {
        title: "Trainer",
        isDisplayed: true,
        highlighted: orderBy === EColumnName.BARN_NAME,
        sortableCell: {
          name: EColumnName.TRAINER_LASTNAME,
          sortOrder: getSortDirection(
            EColumnName.TRAINER_LASTNAME,
            orderBy,
            order
          ),
          sortHandler: handleSort
        }
      }
    ],
    [ERowColumn.HISTORY]: {
      title: "History",
      isDisplayed: isRequestsTab || isExamsTab,
      centered: true
    },
    [ERowColumn.WORK_DATE]: {
      title: "Work Date",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.WORKOUT_REQUEST_DATE,
        sortOrder: getSortDirection(
          EColumnName.WORKOUT_REQUEST_DATE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.REQUESTED]: {
      title: "Requested",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.WORKOUT_REQUEST_CREATION_DATE,
        sortOrder: getSortDirection(
          EColumnName.WORKOUT_REQUEST_CREATION_DATE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RISK_LEVEL]: {
      title: "Risk Level",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.HEALTH_WATCH_STATUS,
        sortOrder: getSortDirection(
          EColumnName.HEALTH_WATCH_STATUS,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.BARN]: {
      title: "Barn #",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.BARN_NAME,
        sortOrder: getSortDirection(EColumnName.BARN_NAME, orderBy, order),
        sortHandler: handleSort
      }
    },
    [ERowColumn.STALL]: {
      title: "Stall #",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.STALL_NUMBER,
        sortOrder: getSortDirection(EColumnName.STALL_NUMBER, orderBy, order),
        sortHandler: handleSort
      }
    },
    [ERowColumn.EXAM_STATUS]: {
      title: "Exam Status",
      isDisplayed: isExamsTab,
      centered: true
    },
    [ERowColumn.CSA]: {
      title: "C.S.A",
      isDisplayed: true,
      centered: true,
      dataTestId: "ageSexColumnHeader"
    },
    [ERowColumn.HSF]: {
      title: "HSF",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.HIGH_SPEED_FURLONGS,
        sortOrder: getSortDirection(
          EColumnName.HIGH_SPEED_FURLONGS,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.DLW]: {
      title: "DLW",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.LAST_WORKOUT_DATE,
        sortOrder: getSortDirection(
          EColumnName.LAST_WORKOUT_DATE,
          orderBy,
          order
        ),
        sortHandler: handleSort,
        isInverted: true
      }
    },
    [ERowColumn.DLR]: {
      title: "DLR",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.LAST_RACE_DATE,
        sortOrder: getSortDirection(EColumnName.LAST_RACE_DATE, orderBy, order),
        sortHandler: handleSort,
        isInverted: true
      }
    },
    [ERowColumn.LAST_EXAM]: {
      title: "Last Exam",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.LAST_EXAM_DATE,
        sortOrder: getSortDirection(EColumnName.LAST_EXAM_DATE, orderBy, order),
        sortHandler: handleSort
      }
    },
    [ERowColumn.ASSIGNED_VET]: {
      title: "Assigned Vet",
      isDisplayed: shouldShowAssignedVetColumn
    },
    [ERowColumn.UNASSIGN]: {
      title: "Un-Assign",
      isDisplayed: isExamsTab,
      centered: true
    },
    [ERowColumn.EXAM]: {
      title: "Exam",
      isDisplayed: isExamsTab,
      centered: true
    },
    [ERowColumn.EXAM_TYPE]: {
      title: "Exam Type",
      isDisplayed: isPassedTab
    },
    [ERowColumn.FLAG_REQUEST]: {
      title: "Flag Request",
      isDisplayed: isRequestsTab || isExamsTab,
      sortableCell: {
        name: EColumnName.IS_FLAGGED,
        sortOrder: getSortDirection(EColumnName.IS_FLAGGED, orderBy, order),
        sortHandler: handleSort
      }
    },
    [ERowColumn.ACTIVE_VET_LISTS]: {
      title: "Active VL",
      isDisplayed: true,
      centered: true,
      sortableCell: {
        name: EColumnName.ACTIVE_VET_LISTS_COUNT,
        sortOrder: getSortDirection(
          EColumnName.ACTIVE_VET_LISTS_COUNT,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RISK_FACTOR_SCORE]: {
      title: matchesDownSx ? "Risk Score" : "Risk Factor Score",
      isDisplayed: isRequestsTab || isExamsTab,
      centered: true,
      sortableCell: {
        name: EColumnName.RISK_FACTOR_SCORE,
        sortOrder: getSortDirection(
          EColumnName.RISK_FACTOR_SCORE,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.RISK_FACTORS]: {
      title: "Risk Factors",
      isDisplayed: true,
      sortableCell: {
        name: EColumnName.RISK_FACTORS_COUNT,
        sortOrder: getSortDirection(
          EColumnName.RISK_FACTORS_COUNT,
          orderBy,
          order
        ),
        sortHandler: handleSort
      }
    },
    [ERowColumn.IMAGE_DIAGNOSTICS_REQUIRED]: {
      title: "Imaging/Diagnostics",
      isDisplayed: isRejectedTab,
      centered: true,
      dataTestId: "diagnosticsColumnHeader"
    },
    [ERowColumn.EXTENDER]: {
      title: " ",
      isDisplayed: true,
      lastTwoColumnsCell: isRequestsTab,
      lastOneColumnCell: isExamsTab
    }
  };

  return (
    <>
      {isRequestsTab && (
        <ActionColumnHeaders
          columns={REQUESTS_TAB_ACTION_COLUMNS}
          refElement={refElement}
        />
      )}
      {isExamsTab && (
        <ActionColumnHeaders
          columns={EXAMS_TAB_ACTION_COLUMNS}
          refElement={refElement}
        />
      )}

      <TableHeader
        tabClassName={columnsClassName}
        columns={columns}
        columnsContent={tableHeaderColumnsContent}
      />
    </>
  );
};

export default React.memo(WorkoutsTableHeader);
